import r2Core from "@core/r2";
import { IChatMessage, type IChatMessageResult } from "@listat/r2-core";
import { IChatActionsData } from "@core/base/chat-action-base";
import { WebChatSetting } from "@core/web/web-chat-setting/web-chat-setting";
import { ChatBase } from "@core/base/chat-base";
import { BehaviorSubject, Observable } from "rxjs";
import { WebChatRepository } from "@core/web/web-chat-repository/web-chat-repository";
import { WebSession } from "@core/web/web-session/web-session";

export class WebChat extends ChatBase implements ChatBase {

    private r2Core = r2Core;
    public $messages: BehaviorSubject<IChatMessage[]> = new BehaviorSubject<IChatMessage[]>([]);

    private isMessagesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private observerMessages = {
        update: this.onMessageUpdate.bind(this),
    }

    constructor(
        private sessionService: WebSession,
        private chatRepository: WebChatRepository,
        public settingService: WebChatSetting,
    ) {
        super()
        this.defineHandlerOnSessionChange();
        this.defineHandlerOnUpdateMessages();
    }

    public $isMessagesLoading(): Observable<boolean> {
        return this.isMessagesLoading.asObservable()
    }

    public getMessages(): Observable<IChatMessage[]> {
        return this.$messages.asObservable();
    }

    public getMessagesValue(): IChatMessage[] {
        return this.$messages.getValue();
    }

    public async sendMessage(message: string, dataAction: IChatActionsData | null): Promise<IChatMessageResult> {
        const newMessage = this.composeMessageWithAction(message);
        const result = await this.r2Core.chat.sendMessage(newMessage);

        return result;
    }

    public async sendMessageWithoutSaving(message: string, dataAction: IChatActionsData | null): Promise<IChatMessageResult> {
        const newMessage = this.composeMessageWithAction(message);
        const result = await this.r2Core.chat.sendMessage(newMessage, false);

        return result;
    }

    public async writeBotMessage(text: string): Promise<void> {
        const messageOptions = {
            plan: {
                availableTokens: 0,
                type: this.constants.SETTING_PLAN_TYPE.TRIAL,
            },
            embeds: [],
            usedTotalTokens: 0,
            usages: []
        };
        this.r2Core.chat.createMessageBot(text, messageOptions);
    }

    public async clearMessages(): Promise<void> {
        await this.r2Core.chat.clearMessages();
    }

    public async init(): Promise<void> {
        const session = this.sessionService.getSession();

        await this.chatRepository.initStorage(
            session.user_email,
            this.settingService
        );

        await Promise.all([
            this.r2Core.chat.initMessages(
                this.chatRepository
            ),
            this.r2Core.chat.initSetting(
                this.chatRepository
            )
        ]);

        this.isMessagesLoading.next(false)
    }

    private composeMessageWithAction(message: string): string {
        const quickAction = this.settingService.getCurrentQuickAction();
        const skill = this.settingService.getCurrentSkill();

        if (quickAction) {
            this.settingService.setCurrentQuickAction(null);
            return message.length === 0 ? quickAction.value : `${quickAction.value}\n${message}`;
        }

        if (skill) {
            this.settingService.setCurrentSkill(null);
            return message.length === 0 ? skill.prompt : `${skill.prompt}\n\n${message}`;
        }

        return message;
    }

    private onMessageUpdate(messages: IChatMessage[]): void {
        this.$messages.next(messages);
        this.chatRepository.writeMessages(messages);
    }

    private defineHandlerOnSessionChange(): void {
        this.sessionService.sessionInfo$.subscribe((info) => {
            if (info && info.user_id) {
                this.init();
            }
        })
    }

    private defineHandlerOnUpdateMessages(): void {
        this.observerMessages = this.r2Core.chat.subscribeOnUpdateMessage(this.observerMessages);
    }

    public override onDestroy(): void {
        this.r2Core.chat.unSubscribeOnUpdateMessage(this.observerMessages)
    }
}
